import type { LucideIcon } from 'lucide-react';
import type React from 'react';
import {
  Alert,
  AlertContent,
  AlertDescription,
  AlertIndicator,
  type AlertStatus,
  AlertTitle,
} from '../tailgrids/core/alert.js';
import { cn } from '../../utils/cn.js';

export type CalloutTone = 'accent' | 'success' | 'warning' | 'danger' | 'teal' | 'purple';

export interface CalloutProps {
  tone: CalloutTone;
  icon?: LucideIcon;
  title?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

/** Our tones on NextAdmin's alert statuses; `teal`/`purple` were decorative variants of an informational note. */
const STATUS: Record<CalloutTone, AlertStatus> = {
  accent: 'info',
  success: 'success',
  warning: 'warning',
  danger: 'error',
  teal: 'info',
  purple: 'default',
};

/**
 * An inline message inside the page (NextAdmin alert): a note, a stale-data warning, a failure with a retry action.
 * `danger` is announced to screen readers as an alert; the other tones are notes.
 */
export function Callout({ tone, icon: Icon, title, actions, children, className }: CalloutProps) {
  const status = STATUS[tone];
  return (
    <Alert
      status={status}
      role={status === 'error' ? 'alert' : 'note'}
      className={cn(
        'max-w-none px-3.5 py-3 has-data-[slot=alert-description]:px-3.5 has-data-[slot=alert-description]:py-3',
        className,
      )}
    >
      <AlertIndicator className="shrink-0">{Icon ? <Icon aria-hidden="true" /> : undefined}</AlertIndicator>
      <AlertContent className="min-w-0 gap-1.5">
        {title && (
          <AlertTitle level={4} className="text-sm leading-5">
            {title}
          </AlertTitle>
        )}
        {children && <AlertDescription className="w-full">{children}</AlertDescription>}
        {actions && <div className="mt-1 flex flex-wrap items-center gap-2">{actions}</div>}
      </AlertContent>
    </Alert>
  );
}
